/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import LexicalContext, {
  defaultConfig,
  LexicalContextProps,
} from '@/components/LexicalEditor/LexicalContext';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import * as React from 'react';
import styles from './comment_editor.module.scss';
import { forwardRef, useImperativeHandle } from 'react';
import ImagesPlugin from '../plugins/ImagesPlugin';
import LinkPlugin from '../plugins/LinkPlugin';
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html';

import Placeholder from '../ui/Placeholder';
import {
  $transformTextToMentionNodes,
  BeautifulMentionNode,
} from 'lexical-beautiful-mentions';

import {
  COMMAND_PRIORITY_HIGH,
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $getSelection,
  $insertNodes,
  $isElementNode,
  $isNodeSelection,
  $isRangeSelection,
  KEY_ARROW_LEFT_COMMAND,
  KEY_ARROW_RIGHT_COMMAND,
  $nodesOfType,
  LexicalNode,
} from 'lexical';
import { $wrapTopLevelInlines } from '@/components/LexicalEditor/utils/wrapTopLevelInlines';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin';
import { ClickableLinkPlugin } from '@lexical/react/LexicalClickableLinkPlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { Stack, SxProps } from '@mui/material';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import MentionPlugin, {
  V1CompatMentionNode,
} from '@/components/LexicalEditor/CommentEditor/MentionPlugin';

import FileDragDropPlugin from '@/components/LexicalEditor/plugins/FileDragDropPlugin';
import { FileListNode } from '@/components/LexicalEditor/nodes/FileListNode';
import { RemoteFile } from '@/components/file-thumbnail/utils';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import DragDropPaste from '@/components/LexicalEditor/plugins/DragDropPastePlugin';
import DeleteResetPlugin from '@/components/LexicalEditor/plugins/DeleteResetPlugin';
import { t } from 'i18next';
import usePasteWithoutFontSizeAndColor from '@/components/LexicalEditor/CommentEditor/usePasteWithoutFontSizeAndColor';
import UploadImageNode from '@/components/LexicalEditor/nodes/UploadImageNode';
import { traverse } from '@/components/LexicalEditor/utils/utils';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { useLexicalEditable } from '@lexical/react/useLexicalEditable';
import { isImageFile } from '@/components/AttachmentFile/utils';
import { insertInlineImageFromFile } from '@/components/LexicalEditor/utils/insertInlineImage';
import { useSnackbar } from '@/hooks/global-tools';
import { mergeRegister } from '@lexical/utils';

/**
 * 'file'(기본) : 모든 첨부를 FileListNode(첨부 카드)로. (게시판/소셜 기존 동작과 동일)
 * 'inline'    : 이미지 파일은 본문 인라인 이미지(ImageResizer)로, 그 외는 FileListNode. (품의 코멘트)
 */
export type CommentImageUploadMode = 'inline' | 'file';

type CommentEditorProps = {
  onFocus?: React.FocusEventHandler<HTMLDivElement>;
  onBlur?: React.FocusEventHandler<HTMLDivElement>;
  onChange?: (value: string) => void;
  disabled?: boolean;
  sx?: SxProps;
  imageUploadMode?: CommentImageUploadMode;
  /**
   * 전자결재 코멘트 여부. true 면 멘션에 팀명 포함 + 저장 HTML 을 레거시 span.mention-name
   * 포맷으로 내보냄(V1CompatMentionNode, 재편집 importDOM 호환 유지).
   */
  isApproval?: boolean;
  placeholder?: string; // (선택) 에디터 placeholder 문구. 미전달 시 기본 `t('내용을 입력해 주세요.')`
};

export type CommentEditorRef = {
  clear: () => void;
  insertMention: () => void;
  update: (content: string) => void;
  openFile: () => void;
  getFiles: () => RemoteFile[];
  isEmpty: () => boolean;
  setData: (htmlContent: string) => void;
  hasUploadingImage: () => boolean;
  hasContent: () => boolean; // 모달 닫기 가드용 — 업로드 완료 이미지만 있으면 false
};

const CommentEditor = forwardRef<CommentEditorRef, CommentEditorProps>(
  (props, ref) => {
    const {
      onChange,
      disabled,
      sx,
      onFocus,
      onBlur,
      imageUploadMode = 'file',
      placeholder,
    } = props;
    const isEditable = useLexicalEditable();
    const [editor] = useLexicalComposerContext();
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const toast = useSnackbar();

    const handleOnChange = () => {
      editor.update(() => {
        const htmlString = $generateHtmlFromNodes(editor, null);
        onChange?.(htmlString);
      });
    };

    const insertFileList = (
      files: File[],
      variant: 'default' | 'approval' = 'default',
    ) => {
      editor.update(() => {
        const selection = $getSelection();
        const listNode = new FileListNode(
          files.map((item) => ({
            file: item,
            status: 'loading',
            link: '',
            name: item.name,
            fileSize: item.size,
          })),
          variant,
        );
        const paragraphNode = $createParagraphNode();

        if (selection && $isRangeSelection(selection)) {
          selection.insertNodes([listNode, paragraphNode]);
        } else {
          $getRoot().selectEnd();
          $insertNodes([listNode, paragraphNode]);
        }

        const nextSibling = paragraphNode.getNextSibling();
        if (
          $isElementNode(nextSibling) &&
          nextSibling.getType() === 'paragraph'
        ) {
          paragraphNode.remove();
          nextSibling.selectStart();
        } else {
          paragraphNode.selectStart();
        }
      });
    };

    React.useEffect(() => {
      const moveSelectionAroundFileNode = (
        direction: 'previous' | 'next',
      ): boolean => {
        const selection = $getSelection();

        if ($isNodeSelection(selection)) {
          const fileNode = selection
            .getNodes()
            .find((node) => node instanceof FileListNode);
          if (!(fileNode instanceof FileListNode)) return false;

          const sibling =
            direction === 'previous'
              ? fileNode.getPreviousSibling()
              : fileNode.getNextSibling();
          if ($isElementNode(sibling)) {
            direction === 'previous'
              ? sibling.selectEnd()
              : sibling.selectStart();
            return true;
          }
          return false;
        }

        if (!$isRangeSelection(selection) || !selection.isCollapsed()) {
          return false;
        }

        const anchorNode = selection.anchor.getNode();
        const topLevelElement = anchorNode.getTopLevelElement();
        if (!topLevelElement || topLevelElement.getType() !== 'paragraph') {
          return false;
        }

        const isAtBoundary =
          direction === 'previous'
            ? selection.anchor.offset === 0 &&
              (anchorNode.is(topLevelElement) ||
                anchorNode.getPreviousSibling() === null)
            : selection.anchor.offset === anchorNode.getTextContentSize() &&
              (anchorNode.is(topLevelElement) ||
                anchorNode.getNextSibling() === null);
        if (!isAtBoundary) return false;

        const sibling =
          direction === 'previous'
            ? topLevelElement.getPreviousSibling()
            : topLevelElement.getNextSibling();
        if (!(sibling instanceof FileListNode)) return false;

        const target =
          direction === 'previous'
            ? sibling.getPreviousSibling()
            : sibling.getNextSibling();
        if ($isElementNode(target)) {
          direction === 'previous' ? target.selectEnd() : target.selectStart();
          return true;
        }

        const paragraphNode = $createParagraphNode();
        direction === 'previous'
          ? sibling.insertBefore(paragraphNode)
          : sibling.insertAfter(paragraphNode);
        direction === 'previous'
          ? paragraphNode.selectEnd()
          : paragraphNode.selectStart();
        return true;
      };

      return mergeRegister(
        editor.registerCommand<KeyboardEvent>(
          KEY_ARROW_LEFT_COMMAND,
          (event) => {
            if (
              event.shiftKey ||
              event.altKey ||
              event.ctrlKey ||
              event.metaKey
            ) {
              return false;
            }

            const handled = moveSelectionAroundFileNode('previous');
            if (handled) event.preventDefault();
            return handled;
          },
          COMMAND_PRIORITY_HIGH,
        ),
        editor.registerCommand<KeyboardEvent>(
          KEY_ARROW_RIGHT_COMMAND,
          (event) => {
            if (
              event.shiftKey ||
              event.altKey ||
              event.ctrlKey ||
              event.metaKey
            ) {
              return false;
            }

            const handled = moveSelectionAroundFileNode('next');
            if (handled) event.preventDefault();
            return handled;
          },
          COMMAND_PRIORITY_HIGH,
        ),
      );
    }, [editor]);

    const handleFilesChanges = async (files: File[]) => {
      // inline 모드(품의 코멘트): 이미지는 본문 인라인, 그 외는 기존 첨부 카드로 분기.
      if (imageUploadMode === 'inline') {
        const imageFiles = files.filter((f) => isImageFile(f.name));
        const otherFiles = files.filter((f) => !isImageFile(f.name));

        for (const file of imageFiles) {
          await insertInlineImageFromFile(editor, file, toast, t);
        }

        if (otherFiles.length) {
          insertFileList(otherFiles, 'approval');
        }
        return;
      }

      // 기본 'file' 모드: 모든 첨부를 FileListNode 로 (게시판/소셜 기존 동작 그대로).
      insertFileList(files);
    };

    // 빈 검사 / 업로드 진행 검사 — isEmpty·hasUploadingImage·hasContent 가 공유.
    const checkIsEmpty = (): boolean => {
      function containsFileListNode(node: LexicalNode): boolean {
        if (
          node instanceof FileListNode ||
          (node.getType && node.getType() === 'upload-image')
        ) {
          return true;
        }

        if ($isElementNode(node)) {
          for (const child of node.getChildren()) {
            if (containsFileListNode(child)) {
              return true;
            }
          }
        }

        return false;
      }

      return editor.getEditorState().read(() => {
        const root = $getRoot();

        if (root.getTextContent().trim().length > 0) {
          return false;
        }

        return !containsFileListNode(root);
      });
    };

    const checkHasUploadingImage = (): boolean => {
      return editor.getEditorState().read(() => {
        const nodes = $nodesOfType(UploadImageNode);
        return nodes.some((n) => n.getStatus() === 'loading');
      });
    };

    useImperativeHandle(ref, () => ({
      clear: () => {
        editor.update(() => {
          const root = $getRoot();
          root.clear();
        });
      },
      update: (content: string) => {
        editor.update(() => {
          const root = $getRoot();
          root.clear();

          const doc = new DOMParser().parseFromString(content, 'text/html');
          const nodes = $generateNodesFromDOM(editor, doc);

          // root 직접 append 경로 — 최상위 인라인은 #282 회피용 문단 감싸기 필수
          root.append(...$wrapTopLevelInlines(nodes));

          $transformTextToMentionNodes(['@']);
        });
      },
      insertMention: () => {
        editor.update(() => {
          const selection = $getSelection();

          if (selection) {
            const textNode = $createTextNode('@');
            $insertNodes([textNode]);
          }
        });
      },
      openFile: async () => {
        fileInputRef.current?.click();
      },
      getFiles: (): RemoteFile[] => {
        const files: RemoteFile[] = [];
        editor.getEditorState().read(() => {
          const root = $getRoot();
          traverse(root, (node) => {
            if (node.getType() === 'file-list') {
              const fl = node as FileListNode;
              files.push(...fl.__files);
            }
          });
        });
        return files;
      },
      isEmpty: checkIsEmpty,
      setData: (htmlContent: string) => {
        editor.update(() => {
          const parser = new DOMParser();
          const dom = parser.parseFromString(htmlContent, 'text/html');
          const nodes = $generateNodesFromDOM(editor, dom);
          const root = $getRoot();
          root.clear();

          // root 직접 append 경로 — 최상위 인라인은 #282 회피용 문단 감싸기 필수
          root.append(...$wrapTopLevelInlines(nodes));
        });
      },
      hasUploadingImage: checkHasUploadingImage,
      hasContent: () => !checkIsEmpty() || checkHasUploadingImage(),
    }));

    // 붙여넣기 시에 fontSize와 color를 제거
    usePasteWithoutFontSizeAndColor(editor);

    return (
      <>
        <Stack
          className={`CommentEditor ${styles.commentEditor}${
            imageUploadMode === 'inline' ? ' approval-comment-editor' : ''
          }`}
          onDragOver={(e) => e.preventDefault()}
          sx={sx}
        >
          <RichTextPlugin
            contentEditable={
              <div className="editor-scroller">
                <ContentEditable
                  className={'ContentEditable__root'}
                  disabled={disabled}
                  onFocus={onFocus}
                  onBlur={onBlur}
                />
              </div>
            }
            placeholder={
              <Placeholder>
                {placeholder ?? t('내용을 입력해 주세요.')}
              </Placeholder>
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          <HistoryPlugin />
          <MentionPlugin />
          <MarkdownShortcutPlugin />
          <ListPlugin />
          <DeleteResetPlugin />
          <ImagesPlugin />
          <LinkPlugin />
          <OnChangePlugin onChange={handleOnChange} />
          {!isEditable && <ClickableLinkPlugin disabled={isEditable} />}
          <FileDragDropPlugin onSelectFiles={handleFilesChanges} />
          <DragDropPaste inline={imageUploadMode === 'inline'} />
        </Stack>
        <input
          type="file"
          multiple
          hidden
          ref={fileInputRef}
          onChange={async (e) => {
            const files = e.target.files ? Array.from(e.target.files) : [];
            if (files.length) await handleFilesChanges(files);
            e.target.value = '';
          }}
        />
      </>
    );
  },
);
CommentEditor.displayName = 'CommentEditor';

const LexicalCommentEditor = forwardRef<
  CommentEditorRef,
  CommentEditorProps & LexicalContextProps
>((props, ref) => {
  const { onChange, isApproval = false, ...otherProps } = props;
  // 전자결재만 V1CompatMentionNode 로 치환 — 저장 HTML 을 레거시 .mention-name+팀명 포맷으로 내보냄.
  const mentionNodes = isApproval
    ? [
        V1CompatMentionNode,
        {
          replace: BeautifulMentionNode,
          with: (node: BeautifulMentionNode) =>
            new V1CompatMentionNode(
              node.getTrigger(),
              node.getValue(),
              node.getData(),
            ),
          withKlass: V1CompatMentionNode,
        },
      ]
    : [BeautifulMentionNode];
  return (
    <LexicalContext
      {...otherProps}
      config={{
        ...defaultConfig,
        namespace: 'commentEditor',
        nodes: [
          ...(defaultConfig.nodes || []),
          ...mentionNodes,
          FileListNode,
        ] as any,
        theme: {
          ...defaultConfig.theme,
          beautifulMentions: {
            '@': 'beautiful-mention',
          },
        },
      }}
    >
      <CommentEditor {...otherProps} ref={ref} onChange={onChange} />
    </LexicalContext>
  );
});
LexicalCommentEditor.displayName = 'LexicalCommentEditor';

export default LexicalCommentEditor;
